import { DEFAULT_PAGE_SIZE } from '@/constants';
import { useCallback, useRef, useState } from 'react';

export type PaginationState = {
  current: number;
  pageSize: number;
  total: number;
};

export type PaginatedListResult<TItem> = {
  data: TItem[];
  total: number;
  page?: number;
  page_size?: number;
  size?: number;
};

type BuildQueryFn<TQuery> = (
  params: Partial<TQuery>,
  lastQuery: TQuery,
  pagination: PaginationState,
) => TQuery;

type UsePaginatedCrudModelOptions<
  TItem,
  TQuery,
  TCreatePayload,
  TUpdatePayload,
  TListResult extends PaginatedListResult<TItem>,
> = {
  defaultPageSize?: number;
  initialQuery: TQuery;
  loadList: (query: TQuery) => Promise<TListResult>;
  buildQuery: BuildQueryFn<TQuery>;
  createItem: (payload: TCreatePayload) => Promise<TItem>;
  updateItem: (id: number, payload: TUpdatePayload) => Promise<TItem>;
  deleteItem: (id: number) => Promise<void>;
};

type UsePaginatedCrudModelResult<
  TItem,
  TQuery,
  TCreatePayload,
  TUpdatePayload,
  TListResult extends PaginatedListResult<TItem>,
> = {
  items: TItem[];
  loading: boolean;
  pagination: PaginationState;
  loadItems: (params?: Partial<TQuery>) => Promise<TListResult>;
  create: (payload: TCreatePayload) => Promise<TItem>;
  update: (id: number, payload: TUpdatePayload) => Promise<TItem>;
  remove: (id: number) => Promise<void>;
};

export const usePaginatedCrudModel = <
  TItem,
  TQuery,
  TCreatePayload,
  TUpdatePayload,
  TListResult extends PaginatedListResult<TItem>,
>({
  defaultPageSize = DEFAULT_PAGE_SIZE,
  initialQuery,
  loadList,
  buildQuery,
  createItem,
  updateItem,
  deleteItem,
}: UsePaginatedCrudModelOptions<
  TItem,
  TQuery,
  TCreatePayload,
  TUpdatePayload,
  TListResult
>): UsePaginatedCrudModelResult<
  TItem,
  TQuery,
  TCreatePayload,
  TUpdatePayload,
  TListResult
> => {
  const [items, setItems] = useState<TItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    current: 1,
    pageSize: defaultPageSize,
    total: 0,
  });

  const lastQueryRef = useRef<TQuery>(initialQuery);
  const paginationRef = useRef<PaginationState>(pagination);
  const loadListRef = useRef(loadList);
  const buildQueryRef = useRef(buildQuery);
  const createItemRef = useRef(createItem);
  const updateItemRef = useRef(updateItem);
  const deleteItemRef = useRef(deleteItem);

  paginationRef.current = pagination;
  loadListRef.current = loadList;
  buildQueryRef.current = buildQuery;
  createItemRef.current = createItem;
  updateItemRef.current = updateItem;
  deleteItemRef.current = deleteItem;

  const loadItems = useCallback(
    async (params: Partial<TQuery> = {}): Promise<TListResult> => {
      setLoading(true);
      try {
        const query = buildQueryRef.current(
          params,
          lastQueryRef.current,
          paginationRef.current,
        );

        const result = await loadListRef.current(query);
        setItems(Array.isArray(result.data) ? result.data : []);

        const normalizedPage = Math.max(result.page ?? 1, 1);
        setPagination({
          current: normalizedPage,
          pageSize:
            result.page_size ??
            result.size ??
            paginationRef.current.pageSize ??
            defaultPageSize,
          total: result.total,
        });

        lastQueryRef.current = query;
        return result;
      } finally {
        setLoading(false);
      }
    },
    [defaultPageSize],
  );

  const create = useCallback(
    async (payload: TCreatePayload) => createItemRef.current(payload),
    [],
  );

  const update = useCallback(
    async (id: number, payload: TUpdatePayload) =>
      updateItemRef.current(id, payload),
    [],
  );

  const remove = useCallback(
    async (id: number) => deleteItemRef.current(id),
    [],
  );

  return {
    items,
    loading,
    pagination,
    loadItems,
    create,
    update,
    remove,
  };
};
